import Component from '@glimmer/component';
import { service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { task, timeout } from 'ember-concurrency';
import { later } from '@ember/runloop';
import randomstring from 'randomstring';

export default class BouncePlayerAbstractComponent extends Component {
  @service currentlyPlaying;
  @service stereo;
  @service session;

  @tracked audioPositionListener = null;
  @tracked _bounce = null;
  @tracked componentElement = null;
  @tracked randomId = null;

  get bounce() {
    return this._bounce || this.args.bounce;
  }

  get sound() {
    return this.currentlyPlaying.getSound(this.soundId);
  }

  set bounce(newBounce) {
    if (
      !!this._bounce &&
      this._bounce.id == newBounce?.id &&
      this._bounce.peaks.length == newBounce?.peaks?.length
    ) {
      return;
    } else if (this._bounce) {
      this.cleanUpCurrentBounce();
    }
    this._bounce = newBounce;
    this.randomId = randomstring.generate(6);
    if (this._bounce) {
      later(() => {
        this.wireUpCurrentBounce();
      });
    }
  }

  @action
  skipToNextTrack() {
    this.currentlyPlaying.skipToNextTrack();
  }

  @action
  setBounce(newBounce, element) {
    //console.log('Abstract: calling setBounce', newBounce.id, newBounce.peaks.length)

    // First we set a null bounce and allow ember to render the empty player again
    this.bounce = null;

    // Then on the next run/render loop we set the new bounce. This forces a re-render which is helpful for a new bounce that's just getting peaks data after being analyzed.
    later(() => {
      //console.log('Abstract: doing the later in setBounce', newBounce.id, newBounce.peaks.length)
      this.bounce = newBounce;
      if (element) {
        this.componentElement = element;
      }
    });
  }

  @action
  unsetBounce(newBounce) {
    this.bounce = null;
  }

  cleanUpCurrentBounce() {
    //console.log('Abstract cleanUpCurrentBounce', this.bounce.id);
    this.cleanupSoundListeners();
  }

  wireUpCurrentBounce() {
    //console.log('Abstract wireUpCurrentBounce', this.bounce.id);
    this.registerSoundListeners();
  }

  get identifier() {
    return `${this.bounce.downloadUrl}?Authorization=Bearer ${this.session.data.authenticated.access_token}`;
  }

  get soundId() {
    return this?.bounce?.soundId;
  }

  get playerId() {
    let pId = `bounce-player-${this.bounce?.id}`;
    if (this.args.idSuffix) {
      pId += this.args.idSuffix;
    }
    pId += `-${this.randomId}`;
    return pId;
  }

  togglePlaySoundTask = task(async () => {
    this.currentlyPlaying.togglePlayBounce(this.bounce);
    this.afterTogglePlaySound(this.sound);
  });

  updateSoundPosition(timeInSeconds) {
    //console.log('setting time ', timeInSeconds);
    if (!timeInSeconds) {
      console.error(
        'We tried to updateSoundPosition with an empty value. Bailing out.',
        timeInSeconds,
      );
      return;
    }
    let sound = this.sound;
    if (sound) {
      let newTimeInMs = timeInSeconds * 1000;
      sound.position = newTimeInMs;
    }
  }

  async afterTogglePlaySound() {
    await this.registerSoundListeners();
  }

  async registerSoundListeners(sound) {
    let realSound = this.sound;
    //console.log('Abstract: fired registerSoundListeners and the sound = ', realSound, this.identifier);

    if (!this.audioPositionListener && realSound) {
      //console.log('Abstract: we need to register a listener for audio-position-changed')
      this.audioPositionListener = realSound.on(
        'audio-position-changed',
        this.audioPositionChanged,
      );
    } else if (this.audioPositionListener) {
      //console.log('Abstract: we already have a listener for audio-position-changed', this.audioPositionListener)
    } else {
      //console.log('Abstract: we do not have a sound', realSound)
    }
  }

  cleanupSoundListeners(sound) {
    let realSound = this.sound;

    //console.log('Abstract: fired cleanupSoundListeners and the sound = ', realSound);
    if (this.audioPositionListener && realSound) {
      //console.log('Abstract: ARE deregistering audio-position-changed handler', this.audioPositionListener);
      realSound.off('audio-position-changed', this.audioPositionChanged);
      this.audioPositionListener = null;
    } else {
      //console.log('Abstract: NOT deregistering the audio-position-changed handler', sound, this.audioPositionListener)
    }
  }

  async loadSound() {
    console.log(
      'BouncePlayerAbstract: loadSound',
      this.bounce.id,
      this.bounce.fileName,
    );
    let randWait = Math.floor(Math.random() * 200); // up to 200 ms
    //console.log('about to wait for ', randWait)
    await timeout(randWait);
    let sound = await this.currentlyPlaying.loadBounce(this.bounce);
    return sound;
  }

  @action
  audioPositionChanged({ sound }) {
    //console.log('audioPositionChanged - Abstract', sound.position);
    if (this.bounce) {
      this.bounce.percentPlayed = (sound.position / sound.duration) * 100;
    }
    //console.log('the sound position = ', sound.position);
    if (this.args.onAudioPositionChanged) {
      this.args.onAudioPositionChanged(sound.position / 1000);
    }
  }
}
